import React, { useState, useEffect } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, List, ListItem, ListItemText,
    IconButton, Paper, Box
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { API_URL } from "../lib/utils";

const PromptsManager = () => {
    const [prompts, setPrompts] = useState([]);
    const [open, setOpen] = useState(false);
    const [newPrompt, setNewPrompt] = useState({ title: '', content: '' });
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        fetchPrompts();
    }, []);

    const fetchPrompts = async () => {
        const response = await fetch(`${API_URL}/api/prompts`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json'
            }
        });
        const data = await response.json();
        setPrompts(data);
    };

    const handleSavePrompt = async () => {
        const method = editMode ? 'PUT' : 'POST';
        const url = editMode 
            ? `${API_URL}/api/prompts/${editId}`
            : `${API_URL}/api/prompts`;

        try {
            console.log('Saving prompt:', { 
                method, 
                url, 
                editMode,
                editId,
                body: newPrompt 
            }); 
            
            const response = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(newPrompt)
            });

            const responseData = await response.json();
            console.log('Server response:', responseData);

            if (!response.ok) {
                throw new Error(responseData.error || 'Failed to save prompt');
            }
            
            // Update the prompts list immediately without fetching
            if (editMode) {
                setPrompts(prompts.map(p => 
                    p._id === editId ? responseData : p
                ));
            } else {
                setPrompts([...prompts, responseData]);
            }
            
            handleCloseDialog();
        } catch (error) {
            console.error('Error saving prompt:', error);
            alert(`Failed to save prompt: ${error.message}`);
        }
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setNewPrompt({ title: '', content: '' });
        setEditMode(false);
        setEditId(null);
    };

    const handleEditPrompt = (prompt) => {
        console.log('Editing prompt:', prompt); // Add debug logging
        setNewPrompt({ title: prompt.title, content: prompt.content });
        setEditMode(true);
        setEditId(prompt._id);
        setOpen(true);
    };

    const handleOpenDialog = () => {
        setNewPrompt({ title: '', content: '' });
        setEditMode(false);
        setEditId(null);
        setOpen(true);
    };

    const handleDeletePrompt = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/prompts/${id}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Failed to delete prompt');
            }
            fetchPrompts();
        } catch (error) {
            console.error('Error deleting prompt:', error);
        }
    };

    const handleReorder = async (orderedIds) => {
        try {
            const response = await fetch(`${API_URL}/api/prompts/reorder`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ orderedIds })
            });

            if (!response.ok) {
                throw new Error('Failed to reorder prompts');
            }

            const updatedPrompts = await response.json();
            setPrompts(updatedPrompts);
        } catch (error) {
            console.error('Error reordering prompts:', error);
            fetchPrompts();
        }
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        if (result.destination.index === result.source.index) return;

        const reorderedPrompts = Array.from(prompts);
        const [movedItem] = reorderedPrompts.splice(result.source.index, 1);
        reorderedPrompts.splice(result.destination.index, 0, movedItem);

        setPrompts(reorderedPrompts);

        const orderedIds = reorderedPrompts.map(p => p._id);
        handleReorder(orderedIds);
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{ mb: 2 }}
            >
                Add New Prompt
            </Button>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="prompts-list">
                    {(provided) => (
                        <List
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {prompts.map((prompt, index) => (
                                <Draggable
                                    key={prompt._id}
                                    draggableId={prompt._id}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <ListItem
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                mb: 1,
                                                bgcolor: snapshot.isDragging ? 'action.selected' : 'background.paper',
                                            }}
                                        >
                                            <Box
                                                {...provided.dragHandleProps}
                                                sx={{
                                                    mr: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'grab',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                <DragIndicatorIcon />
                                            </Box>
                                            <ListItemText
                                                primary={prompt.title}
                                                secondary={prompt.content}
                                            />
                                            <Box sx={{ display: 'flex', ml: 1 }}>
                                                <IconButton
                                                    onClick={() => handleEditPrompt(prompt)}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleDeletePrompt(prompt._id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </List>
                    )}
                </Droppable>
            </DragDropContext>

            <Dialog 
                open={open} 
                onClose={handleCloseDialog}
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>{editMode ? 'Edit Prompt' : 'Add New Prompt'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Title"
                        fullWidth
                        value={newPrompt.title}
                        onChange={(e) => setNewPrompt({...newPrompt, title: e.target.value})}
                    />
                    <TextField
                        margin="dense"
                        label="Prompt Content"
                        fullWidth
                        multiline
                        rows={4}
                        value={newPrompt.content}
                        onChange={(e) => setNewPrompt({...newPrompt, content: e.target.value})}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSavePrompt}>{editMode ? 'Save' : 'Add'}</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default PromptsManager;
